/**
 * PopOver class.
 */
export default class PopOver {
  /**
   * Initial expanded state.
   * @private
   *
   * @type {Boolean}
   */
  #expandedState = false;

  /**
   * Get the target element based on the controller's `aria-controls` attribute.
   *
   * @param  {HTMLButtonElement} controller The component's controlling element.
   * @return {HTMLElement|null}
   */
  static getTargetElement(controller) {
    if (!controller.hasAttribute('aria-controls')) {
      const message = 'The component element is missing the required \'aria-controls\' attribute';
      throw new Error(`Configuration error: ${message}`);
    }

    const targetId = controller.getAttribute('aria-controls');
    const target = document.getElementById(targetId);

    if (target === null) {
      const message = `A target element with ID of '${targetId}' is not found`;
      throw new Error(`Configuration error: ${message}`);
    }

    return target;
  }

  /**
   * Start the component
   */
  constructor(controller) {
    // Collect elements.
    this.controller = controller;
    this.target = this.constructor.getTargetElement(controller);

    this.init();
  }

  /**
   * Sets up the component's DOM attributes and event listeners.
   */
  init = () => {
    // Patch button role for non-button controller.
    if (this.controller.nodeName !== 'BUTTON') {
      this.controller.setAttribute('role', 'button');
      this.controller.addEventListener('keydown', this.handleControllerKeydown);
    }

    // The button is collapsed by default.
    requestAnimationFrame(() => {
      this.controller.setAttribute('aria-expanded', 'false');
    });

    /*
     * Set the taget as hidden by default. Using the `aria-hidden` attribute,
     * rather than the `hidden` attribute, means authors must hide the target
     * element via CSS.
     */
    requestAnimationFrame(() => {
      this.target.setAttribute('aria-hidden', 'true');
    });

    // Add event listeners
    this.controller.addEventListener('click', this.handleControllerClick);
    this.target.addEventListener('focusout', this.handleTargetFocusout);
  }

  /**
   * Sets expanded state.
   *
   * @param {bool} newState The expected expanded state.
   */
  set expanded(newExpanded) {
    this.#expandedState = newExpanded;

    requestAnimationFrame(() => {
      this.controller.setAttribute('aria-expanded', this.expanded);
      this.target.setAttribute('aria-hidden', !this.expanded);
    });

    if (this.expanded) {
      document.body.addEventListener('keydown', this.handleBodyKeydown);
    } else {
      document.body.removeEventListener('keydown', this.handleBodyKeydown);
    }
  }

  /**
   * Returns expanded state.
   *
   * @return {bool} The current expanded state.
   */
  get expanded() {
    return this.#expandedState;
  }

  /**
   * Handle controller clicks.
   *
   * @param {Event} event The event object.
   */
  handleControllerClick = (event) => {
    event.preventDefault();

    this.toggle();
  };

  /**
   * Treats the Spacebar and Return keys as clicks in case the controller is
   * not a <button>.
   *
   * @param {Event} event The event object.
   */
  handleControllerKeydown = (event) => {
    const { code } = event;

    if (['Space', 'Enter'].includes(code)) {
      event.preventDefault();

      this.toggle();
    }
  }

  /**
   * Handle target element focusout.
   *
   * @param {Event} event The Event object.
   */
  handleTargetFocusout = (event) => {
    if (!this.target.contains(event.relatedTarget)) {
      this.expanded = false;
    }
  };

  /**
   * Hides the target when the Escape key is pressed.
   *
   * @param {Event} event The Event object.
   */
  handleBodyKeydown = (event) => {
    const { code } = event;

    if (code === 'Escape' && this.expanded) {
      event.preventDefault();

      // Hide the target when the Escape key is pressed.
      requestAnimationFrame(() => {
        this.hide();
      });
    }
  };

  /**
   * Update component state to show the target element.
   */
  show = () => {
    this.expanded = true;
  }

  /**
   * Update component state to hide the target element.
   */
  hide = () => {
    this.expanded = false;

    if (this.target.contains(document.activeElement)) {
      // Move focus to the controller.
      this.controller.focus();
    }
  }

  /**
   * Toggle component state.
   */
  toggle = () => {
    this.expanded = !this.expanded;
  }

  /**
   * Remove all attributes and event listeners added by this class.
   */
  destroy = () => {
    // Remove target attributes.
    requestAnimationFrame(() => {
      this.target.removeAttribute('aria-hidden');
    });

    // Remove controller attributes.
    requestAnimationFrame(() => {
      this.controller.removeAttribute('aria-expanded');
    });

    if (this.controller.nodeName !== 'BUTTON') {
      this.controller.removeAttribute('role');
      this.controller.removeEventListener('keydown', this.handleControllerKeydown);
    }

    // Remove event listeners.
    this.controller.removeEventListener('click', this.handleControllerClick);
    this.target.removeEventListener('focusout', this.handleTargetFocusout);
    document.body.removeEventListener('keydown', this.handleBodyKeydown);

    // Reset initial state by setting the private field so the setter doesn't run.
    this.#expandedState = false;
  }
}
